import { useMemo } from 'react';
import useSWR, { mutate } from 'swr';

const endpoints = {
  key: 'app-notifications'
};

// Notification types
export const NOTIFICATION_TYPES = {
  SYNC_SUCCESS: 'sync_success',
  SYNC_FAILED: 'sync_failed',
  SYNC_PARTIAL: 'sync_partial',
  SYNC_NO_DATA: 'sync_no_data',
  SYNC_IN_PROGRESS: 'sync_in_progress',
  SYNC_OTHER_THERAPIST: 'sync_other_therapist',
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  SUCCESS: 'success'
};

const initialState = {
  notifications: []
};

/**
 * Hook to get all notifications
 */
export function useGetNotifications() {
  const { data } = useSWR(endpoints.key, () => initialState, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  const memoizedValue = useMemo(() => {
    const notifications = data?.notifications || [];
    const unreadCount = notifications.filter(n => !n.read).length;
    return {
      notifications,
      unreadCount
    };
  }, [data]);

  return memoizedValue;
}

/**
 * Add a new notification
 * @param {Object} notification - The notification object
 * @param {string} notification.type - Notification type from NOTIFICATION_TYPES
 * @param {string} notification.title - Notification title
 * @param {string} notification.message - Notification message
 * @param {string} notification.color - Color: 'success', 'error', 'warning', 'info'
 */
export function addNotification(notification) {
  const newNotification = {
    id: Date.now() + Math.random(),
    timestamp: new Date().toISOString(),
    read: false,
    ...notification
  };

  mutate(
    endpoints.key,
    (currentState) => {
      const notifications = currentState?.notifications || [];
      return {
        notifications: [newNotification, ...notifications].slice(0, 50) // Keep max 50
      };
    },
    false
  );
}

/**
 * Mark a notification as read
 */
export function markNotificationRead(id) {
  mutate(
    endpoints.key,
    (currentState) => {
      const notifications = (currentState?.notifications || []).map(n =>
        n.id === id ? { ...n, read: true } : n
      );
      return { notifications };
    },
    false
  );
}

/**
 * Mark all notifications as read
 */
export function markAllNotificationsRead() {
  mutate(
    endpoints.key,
    (currentState) => {
      const notifications = (currentState?.notifications || []).map(n => ({
        ...n,
        read: true
      }));
      return { notifications };
    },
    false
  );
}

/**
 * Remove a notification
 */
export function removeNotification(id) {
  mutate(
    endpoints.key,
    (currentState) => {
      const notifications = (currentState?.notifications || []).filter(n => n.id !== id);
      return { notifications };
    },
    false
  );
}

/**
 * Clear all notifications
 */
export function clearAllNotifications() {
  mutate(endpoints.key, { notifications: [] }, false);
}

/**
 * Helper to format time ago
 */
export function getTimeAgo(timestamp) {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
}
